/**
 * CounterPriorityOrderSection ("Priority Counters") — Settings control for
 * which zone (and which counters within it) opens first when the auto-assign
 * engine needs a new counter.
 *
 * Scoped to the current session_id — shared by every user connected to that
 * session, NOT a shared admin default and NOT per-individual-user. "Use
 * default order" (on initially) shows the system's historical order
 * read-only, so the user always sees what's actually in effect. Turning it
 * off reveals an editable rule list — one or more HHMM-HHMM time ranges,
 * each with its own zone order and per-zone counter order — with any time
 * not covered by a rule still falling back to the default order.
 *
 * Zones (including the motor-counter zone — never hardcoded to just the 4
 * car zones) are collapsed by default so reordering them stays a short,
 * glanceable list; expanding one reveals its full counter order as a
 * drag-to-reorder list, which scales far better than a pair of up/down
 * buttons per counter once a zone has 8-10+ of them. Priority within a list
 * is shown by colour intensity (darkest indigo = highest) rather than a
 * "HIGHEST" label on every row, so it reads at a glance.
 */
import { useEffect, useId, useRef, useState } from "react";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import { getModeConfig, getMotorZoneRange, formatCounterLabel, generateTimeSlots, getPriorityShade } from "@/config/modeConfig";
import type { PriorityOrderConfig, PriorityOrderRule, ZoneName } from "@/types/api";

interface Props {
  operation_mode?: string;
  shift_type?: string;
}

const DEFAULT_ZONE_ORDER: ZoneName[] = ["motor", "zone2", "zone3", "zone1", "zone4"];
const ZONE_LABELS: Record<ZoneName, string> = {
  motor: "Motor",
  zone1: "Zone 1",
  zone2: "Zone 2",
  zone3: "Zone 3",
  zone4: "Zone 4",
};

let _ruleId = 0;
const newRuleId = () => `pr-${Date.now()}-${++_ruleId}`;

/**
 * A fresh rule covering the full shift by default. start/end must be real
 * options from the shift's own time-slot list (not a sentinel like "0000"-
 * "2359") — the <select> silently falls back to its first option otherwise,
 * showing a misleading time range even though the underlying value is fine.
 */
function defaultRule(label: string, shiftSlots: string[]): PriorityOrderRule {
  const start = shiftSlots[0] ?? "0000";
  const end = shiftSlots[shiftSlots.length - 1] ?? "2359";
  return { id: newRuleId(), label, start, end, zone_order: [...DEFAULT_ZONE_ORDER], counter_order: {}, rebalance_zones: true };
}

const DEFAULT_CONFIG: PriorityOrderConfig = { use_default: true, rules: [] };

/** Move the array element at `from` to index `to`, returning a new array. */
function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length || from === to) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * A zone's counters in the DEFAULT order, derived from the mode config (never
 * a hardcoded list of counter IDs — only the direction is fixed): car zones
 * go highest-first (e.g. AC10 before AC1); the motor zone goes lowest-first
 * (e.g. arrival's AM41 before AM43) — a fixed, small, named set where
 * "lowest first" is the natural default, unlike a numeric car-zone range.
 */
function defaultZoneCounters(mode: string | undefined, zone: ZoneName): number[] {
  if (zone === "motor") {
    const [lo, hi] = getMotorZoneRange(mode);
    return Array.from({ length: hi - lo }, (_, i) => lo + i + 1);
  }
  const { zones } = getModeConfig(mode);
  const idx = Number(zone[4]) - 1;
  const [lo, hi] = zones[idx] ?? [0, 0];
  return Array.from({ length: hi - lo }, (_, i) => hi - i);
}

const sameOrder = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

// ─── iOS-style on/off switch ────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 40,
        height: 23,
        borderRadius: 999,
        border: "none",
        padding: 2,
        background: checked ? "#4338ca" : "#d1d5db",
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "default" : "pointer",
        transition: "background 0.18s ease",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 19,
          height: 19,
          borderRadius: "50%",
          background: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          transform: checked ? "translateX(17px)" : "translateX(0)",
          transition: "transform 0.18s ease",
          display: "block",
        }}
      />
    </button>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string | string[];
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const lines = !description ? [] : Array.isArray(description) ? description : [description];
  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 16, padding: "9px 2px",
        // Fade the text alongside the switch — a crisp label next to a greyed-out
        // control reads as "still editable" when the whole row is read-only.
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}</div>
        {lines.map((line, i) => (
          <div key={i} style={{ fontSize: 11, color: "#9ca3af", marginTop: i === 0 ? 1 : 4, maxWidth: 460 }}>
            {line}
          </div>
        ))}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// ─── Generic drag-to-reorder (Pointer Events, no library) ─────────────────

/** Identifies a row as a drop target for its own list (see useDragReorder). */
type DragRowProps = { "data-drag-group": string; "data-drag-index": number };

/**
 * Reordering built on Pointer Events rather than HTML5 drag-and-drop.
 *
 * iOS Safari never fires dragstart/dragover/drop — HTML5 DnD is desktop-only —
 * so the previous implementation left the whole reorder UI dead on iPad and
 * iPhone. Pointer Events are one API covering touch, pen and mouse, so there
 * is a single code path here instead of a touch fallback beside a mouse one.
 *
 * Dragging starts from the ⠿ handle, not the whole row: a zone row is also a
 * tap target (tap to expand), and on touch there is no way to tell a tap from
 * the beginning of a drag if both live on the same element. The handle carries
 * `touch-action: none` so the browser hands us the gesture instead of scrolling
 * the page with it.
 *
 * The handle is a real <button>, so it is reachable by keyboard and screen
 * reader: Arrow Up/Down move the row without any pointer at all.
 *
 * Returns per-row props (drop-target identification), per-handle props (the
 * gesture itself), and which index is the current drop target.
 */
function useDragReorder<T>(items: T[], onReorder: (next: T[]) => void, disabled: boolean) {
  // Each hook instance gets its own group so a zone drag never targets a
  // counter row from an expanded zone nested inside the same list.
  const group = useId();
  const fromRef = useRef<number | null>(null);
  const overRef = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const setOver = (idx: number | null) => {
    overRef.current = idx;
    setOverIndex(idx);
  };

  const end = (commit: boolean) => {
    const from = fromRef.current;
    const to = overRef.current;
    fromRef.current = null;
    setDragIndex(null);
    setOver(null);
    if (commit && from !== null && to !== null && from !== to) {
      onReorder(moveItem(items, from, to));
    }
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    onReorder(moveItem(items, from, to));
  };

  const rowProps = (idx: number): DragRowProps => ({ "data-drag-group": group, "data-drag-index": idx });

  const handleProps = (idx: number) =>
    disabled
      ? {}
      : {
          onPointerDown: (e: React.PointerEvent) => {
            // Keep the row's own onClick (expand/collapse) out of it.
            e.stopPropagation();
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            fromRef.current = idx;
            setDragIndex(idx);
            setOver(idx);
          },
          onPointerMove: (e: React.PointerEvent) => {
            if (fromRef.current === null) return;
            // Pointer capture keeps events coming to the handle, so ask the
            // document what is actually under the finger.
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const row = el?.closest<HTMLElement>("[data-drag-index]");
            if (!row || row.dataset.dragGroup !== group) return;
            const next = Number(row.dataset.dragIndex);
            if (!Number.isNaN(next) && next !== overRef.current) setOver(next);
          },
          onPointerUp: () => end(true),
          onPointerCancel: () => end(false),
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
            e.preventDefault();
            e.stopPropagation();
            move(idx, e.key === "ArrowUp" ? idx - 1 : idx + 1);
          },
          style: { touchAction: "none" as const },
        };

  return { rowProps, handleProps, overIndex, dragIndex };
}

/**
 * The grab handle. A button (not a span) so keyboard users can focus it and
 * reorder with the arrow keys; sized to stay a comfortable touch target.
 */
function DragHandle({
  color,
  label,
  handleProps,
}: {
  color?: string;
  label: string;
  handleProps: React.HTMLAttributes<HTMLButtonElement>;
}) {
  return (
    <button
      type="button"
      {...handleProps}
      title="Drag, or focus and use ↑ ↓, to reorder"
      aria-label={`Reorder ${label}. Use arrow up and arrow down keys.`}
      style={{
        color: color ?? "#9ca3af",
        fontSize: 15,
        letterSpacing: -2,
        cursor: "grab",
        flexShrink: 0,
        lineHeight: 1,
        opacity: 0.85,
        background: "none",
        border: "none",
        padding: "6px 4px",
        margin: "-6px -2px",
        touchAction: "none",
        ...(handleProps.style ?? {}),
      }}
    >
      ⠿
    </button>
  );
}

// ─── Counter row (shown when a zone is expanded) ───────────────────────────
// Priority shown by the rank number alone — zones already carry the colour
// coding, so repeating it per counter would be one signal too many.

function CounterRow({
  counter,
  mode,
  rank,
  disabled,
  isOver,
  isDragging,
  rowProps,
  handleProps,
}: {
  counter: number;
  mode: string | undefined;
  rank: number;
  disabled: boolean;
  isOver: boolean;
  isDragging: boolean;
  rowProps: DragRowProps;
  handleProps: React.HTMLAttributes<HTMLButtonElement>;
}) {
  const label = formatCounterLabel(counter, mode);
  return (
    <div
      {...rowProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "5px 10px",
        borderRadius: 5,
        marginBottom: 3,
        background: isOver ? "#fde68a" : "white",
        border: isOver ? "1px dashed #d97706" : "1px solid #e5e7eb",
        color: "#374151",
        opacity: disabled ? 0.6 : isDragging ? 0.5 : 1,
      }}
    >
      {!disabled && <DragHandle label={label} handleProps={handleProps} />}
      <span style={{ width: 16, textAlign: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, color: rank === 1 ? "#4338ca" : "#9ca3af" }}>
        {rank}
      </span>
      <span style={{ fontSize: 13, fontWeight: rank === 1 ? 700 : 500 }}>{label}</span>
    </div>
  );
}

// ─── Zone row (collapsed by default; expand to reorder its counters) ──────

function ZoneRow({
  zone,
  rank,
  total,
  rule,
  mode,
  disabled,
  expanded,
  isOver,
  isDragging,
  rowProps,
  handleProps,
  onToggleExpand,
  onReorderCounters,
}: {
  zone: ZoneName;
  rank: number;
  total: number;
  rule: PriorityOrderRule;
  mode: string | undefined;
  disabled: boolean;
  expanded: boolean;
  isOver: boolean;
  isDragging: boolean;
  rowProps: DragRowProps;
  handleProps: React.HTMLAttributes<HTMLButtonElement>;
  onToggleExpand: () => void;
  onReorderCounters: (zone: ZoneName, next: number[]) => void;
}) {
  const defaultCtrs = defaultZoneCounters(mode, zone);
  const counters = rule.counter_order[zone] ?? defaultCtrs;
  const isCustom = !sameOrder(counters, defaultCtrs);
  // Read-only (default order) still shows the priority gradient — just muted via
  // the row's opacity below — so rank stays glanceable even when nothing's editable.
  const shade = getPriorityShade(rank, total);
  const onWhite = shade.textColor !== "#FFFFFF";

  const {
    rowProps: counterRowProps,
    handleProps: counterHandleProps,
    overIndex: counterOverIndex,
    dragIndex: counterDragIndex,
  } = useDragReorder(counters, (next) => onReorderCounters(zone, next), disabled);

  return (
    <div
      style={{
        marginBottom: 6,
        borderRadius: 7,
        overflow: "hidden",
        boxShadow: isOver ? "0 0 0 2px #d97706" : "0 1px 2px rgba(15,23,42,0.06)",
      }}
    >
      <div
        {...rowProps}
        onClick={onToggleExpand}
        role="button"
        aria-expanded={expanded}
        style={{
          display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", cursor: "pointer", userSelect: "none",
          background: shade.bg, color: shade.textColor, border: shade.border,
          opacity: disabled ? 0.55 : isDragging ? 0.5 : 1,
        }}
      >
        {!disabled && (
          <DragHandle
            color={onWhite ? undefined : "rgba(255,255,255,0.75)"}
            label={ZONE_LABELS[zone]}
            handleProps={handleProps}
          />
        )}
        <span style={{ fontSize: 13, fontWeight: 700, minWidth: 52 }}>{ZONE_LABELS[zone]}</span>
        <span style={{ fontSize: 12, opacity: onWhite ? 0.75 : 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {formatCounterLabel(counters[0], mode)} first · {counters.length} counters
        </span>
        {isCustom && (
          <span
            title="Customized from default order"
            style={{
              width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
              background: onWhite ? "#4338ca" : "white", opacity: onWhite ? 1 : 0.9,
            }}
          />
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, flexShrink: 0, opacity: onWhite ? 0.6 : 0.8 }}>
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: "8px 10px 10px 10px", background: "#f8fafc" }}>
          {!disabled && counters.length > 1 && (
            <div style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 6px" }}>
              Drag ⠿ (or focus it and press ↑ ↓) to reorder — lower number = higher priority.
            </div>
          )}
          {counters.map((c, i) => (
            <CounterRow
              key={c}
              counter={c}
              mode={mode}
              rank={i + 1}
              disabled={disabled}
              isOver={counterOverIndex === i}
              isDragging={counterDragIndex === i}
              rowProps={counterRowProps(i)}
              handleProps={counterHandleProps(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Rule editor (one time range: header + zone list) ─────────────────────

function RuleEditor({
  rule,
  mode,
  slots,
  disabled,
  onChange,
  onRemove,
  removable,
}: {
  rule: PriorityOrderRule;
  mode: string | undefined;
  slots: string[];
  disabled: boolean;
  onChange: (next: PriorityOrderRule) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  // Zones start collapsed — this resets naturally per rule since RuleEditor
  // is keyed by rule.id, so a freshly-added time range always starts tidy.
  const [expandedZones, setExpandedZones] = useState<Set<ZoneName>>(new Set());

  const toggleZoneExpand = (zone: ZoneName) =>
    setExpandedZones((prev) => {
      const next = new Set(prev);
      next.has(zone) ? next.delete(zone) : next.add(zone);
      return next;
    });

  const reorderZones = (next: ZoneName[]) => onChange({ ...rule, zone_order: next });

  const reorderCounters = (zone: ZoneName, next: number[]) =>
    onChange({ ...rule, counter_order: { ...rule.counter_order, [zone]: next } });

  const {
    rowProps: zoneRowProps,
    handleProps: zoneHandleProps,
    overIndex: zoneOverIndex,
    dragIndex: zoneDragIndex,
  } = useDragReorder(rule.zone_order, reorderZones, disabled);

  const coversFullShift = rule.start === slots[0] && rule.end === slots[slots.length - 1];

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        marginBottom: 10,
        background: "white",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      {/* Header: optional label and the time range share one compact line */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
        <input
          value={rule.label}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, label: e.target.value })}
          placeholder="Label (optional)"
          style={{
            flex: "1 1 120px",
            minWidth: 90,
            padding: "1px 2px",
            border: "none",
            borderBottom: "1px solid transparent",
            fontSize: 13,
            fontWeight: 700,
            color: disabled ? "#9ca3af" : "#1f2937",
            background: "transparent",
            outline: "none",
          }}
          onFocus={(e) => { e.currentTarget.style.borderBottomColor = "#c7d2fe"; }}
          onBlur={(e) => { e.currentTarget.style.borderBottomColor = "transparent"; }}
        />
        <div
          style={{
            display: "flex", alignItems: "center", gap: 2, borderRadius: 999,
            padding: "2px 8px", background: disabled ? "#f3f4f6" : "#eef2ff", flexShrink: 0,
          }}
        >
          <select
            value={rule.start}
            disabled={disabled}
            onChange={(e) => onChange({ ...rule, start: e.target.value })}
            style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 700, color: disabled ? "#9ca3af" : "#4338ca" }}
          >
            {slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ color: disabled ? "#9ca3af" : "#4338ca", fontSize: 12 }}>–</span>
          <select
            value={rule.end}
            disabled={disabled}
            onChange={(e) => onChange({ ...rule, end: e.target.value })}
            style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 700, color: disabled ? "#9ca3af" : "#4338ca" }}
          >
            {slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {!disabled && removable && (
          <button
            onClick={onRemove}
            title="Remove this time range"
            style={{ padding: "2px 7px", background: "none", border: "1px solid #fca5a5", borderRadius: 4, color: "#ef4444", cursor: "pointer", fontSize: 12, lineHeight: 1.3, flexShrink: 0 }}
          >
            ✕
          </button>
        )}
        {/* Only meaningful when this range leaves part of the shift uncovered */}
        {!coversFullShift && (
          <div style={{ flexBasis: "100%", fontSize: 11, color: "#9ca3af" }}>
            All other timings still follow the default order.
          </div>
        )}
      </div>

      <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #f1f5f9" }}>
        <ToggleRow
          label="Balance across Zones"
          description={[
            "On: Always assign new counters to the most vacant zone. If all zones are equally filled, assign new counters according to priority list.",
            "Off: Always assign new counters according to priority list. It's fine if some zones have more counters opened.",
          ]}
          checked={rule.rebalance_zones}
          onChange={(v) => onChange({ ...rule, rebalance_zones: v })}
          disabled={disabled}
        />
        <div style={{ fontSize: 11, color: "#9ca3af", padding: "0 2px", opacity: disabled ? 0.55 : 1 }}>
          Darkest shade = highest priority
        </div>
      </div>

      {rule.zone_order.map((zone, idx) => (
        <ZoneRow
          key={zone}
          zone={zone}
          rank={idx + 1}
          total={rule.zone_order.length}
          rule={rule}
          mode={mode}
          disabled={disabled}
          expanded={expandedZones.has(zone)}
          isOver={zoneOverIndex === idx}
          isDragging={zoneDragIndex === idx}
          rowProps={zoneRowProps(idx)}
          handleProps={zoneHandleProps(idx)}
          onToggleExpand={() => toggleZoneExpand(zone)}
          onReorderCounters={reorderCounters}
        />
      ))}
    </div>
  );
}

// ─── Top-level section ─────────────────────────────────────────────────────

export function CounterPriorityOrderSection({ operation_mode, shift_type }: Props) {
  const { notifySuccess, notifyError, notifyInfo } = useNotification();
  const [config, setConfig] = useState<PriorityOrderConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const shiftKey = shift_type === "night" ? "night" : "morning";
  const slots = generateTimeSlots(shiftKey, 1);

  useEffect(() => {
    apiClient
      .getPriorityOrder()
      .then((cfg) =>
        setConfig({
          use_default: cfg.use_default,
          rules: cfg.rules?.length
            ? cfg.rules.map((r) => ({ ...r, rebalance_zones: r.rebalance_zones ?? true }))
            : [defaultRule("All day", slots)],
        }),
      )
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation_mode, shift_type]);

  const updateRule = (idx: number, next: PriorityOrderRule) =>
    setConfig((prev) => ({ ...prev, rules: prev.rules.map((r, i) => (i === idx ? next : r)) }));

  const removeRule = (idx: number) =>
    setConfig((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== idx) }));

  const addRule = () =>
    setConfig((prev) => ({ ...prev, rules: [...prev.rules, defaultRule("New time range", slots)] }));

  const toggleUseDefault = (useDefault: boolean) =>
    setConfig((prev) => ({
      ...prev,
      use_default: useDefault,
      rules: prev.rules.length ? prev.rules : [defaultRule("All day", slots)],
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.setPriorityOrder(config);
      notifySuccess("Priority counters saved.");
    } catch (err) {
      notifyError((err as Error)?.message ?? "Failed to save priority counters.");
    } finally {
      setSaving(false);
    }
  };

  // Resetting a section already on its defaults changes nothing on screen, which
  // reads as a dead button — so the outcome is always reported, neutrally:
  // nothing is saved yet, only the form has moved.
  const handleReset = () => {
    if (config.use_default) {
      notifyInfo("Counter priority order is already on the built-in default.", false);
      return;
    }
    setConfig({ use_default: true, rules: [defaultRule("All day", slots)] });
    notifyInfo("Counter priority order reset to the built-in default — press Save to apply.", false);
  };

  if (!loaded) return null;

  // Display rules: when defaulting, show the single system-default rule read-only
  // so the user can see exactly what's in effect without it being editable.
  const displayRules = config.use_default ? [defaultRule("All day", slots)] : config.rules;

  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
      <div style={{ borderBottom: "1px solid #e2e8f0", marginBottom: 4 }}>
        <ToggleRow
          label="Use Default"
          checked={config.use_default}
          onChange={toggleUseDefault}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        {displayRules.map((rule, idx) => (
          <RuleEditor
            key={rule.id}
            rule={rule}
            mode={operation_mode}
            slots={slots}
            disabled={config.use_default}
            onChange={(next) => updateRule(idx, next)}
            onRemove={() => removeRule(idx)}
            removable={config.rules.length > 1}
          />
        ))}
      </div>

      {!config.use_default && (
        <button
          onClick={addRule}
          style={{ padding: "4px 10px", background: "none", border: "1px dashed #9ca3af", borderRadius: 4, color: "#6b7280", cursor: "pointer", fontSize: 12, marginBottom: 8 }}
        >
          + Add time range
        </button>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button
          onClick={handleReset}
          style={{ padding: "6px 14px", background: "white", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: "6px 14px", background: "#667eea", color: "white", border: "none", borderRadius: 6, fontWeight: 600, cursor: saving ? "default" : "pointer", fontSize: 13, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
